import { MdOutlineInfo } from 'react-icons/md';
import { Tooltip } from '@ds';
import { ScheduleStatusMode } from '@lp/features/Scheduler/types';
import { getScheduleStatusMode } from '@lp/features/Scheduler/utils';
import { MonthSchedule } from '@/api/apis/resources/schedule';

type ScheduleStatusBadgeProps = {
  monthSchedule?: MonthSchedule;
  readonly?: boolean;
  disableAutogeneration?: boolean;
  /** When true, tooltip text says "this period" instead of "this month". */
  isPeriodMode?: boolean;
};

const STATUS_CONFIG: Record<ScheduleStatusMode, { label: string; className: string }> = {
  [ScheduleStatusMode.Published]: {
    label: 'Published',
    className: 'bg-base-60',
  },
  [ScheduleStatusMode.PrePlanning]: {
    label: 'Pre-Planning',
    className: 'bg-warning-80',
  },
  [ScheduleStatusMode.Planning]: {
    label: 'On Planning',
    className: 'bg-info-70',
  },
};

export const getScheduleStatusTooltipText = (
  status: ScheduleStatusMode,
  readonly?: boolean,
  disableAutogeneration?: boolean,
  isPeriodMode?: boolean,
): string | null => {
  const period = isPeriodMode ? 'this period' : 'this month';
  switch (status) {
    case ScheduleStatusMode.Published:
      return 'Schedule is Published. Visible to all users and used for automatic and manual assignments. Any changes must be requested through the Schedule Manager.';
    case ScheduleStatusMode.PrePlanning:
      return readonly
        ? 'Schedule is in Pre-Planning status. The schedule will be available for assignments after the Schedule Manager has planned and published it.'
        : 'Schedule is in Pre-Planning status. Visible to all users but not available for assignments. Proceed with planning to start building the schedule.';
    case ScheduleStatusMode.Planning:
      if (readonly) {
        return 'Planning Mode started. The schedule will be available for assignments after the Schedule Manager has published it.';
      }
      return disableAutogeneration
        ? `Planning Mode started. Build the schedule manually, then publish the schedule to enable resource assignments for ${period}.`
        : `Planning Mode started. Shift requests are disabled. Use Autogeneration or build the schedule manually, then publish the schedule to enable resource assignments for ${period}.`;
    default:
      return null;
  }
};

export const ScheduleStatusBadge = ({
  monthSchedule,
  readonly,
  disableAutogeneration,
  isPeriodMode,
}: ScheduleStatusBadgeProps) => {
  const status = getScheduleStatusMode(monthSchedule);

  if (!status) return null;

  const { label, className } = STATUS_CONFIG[status];
  const tooltipText = getScheduleStatusTooltipText(status, readonly, disableAutogeneration, isPeriodMode);

  return (
    <Tooltip showArrow placement="bottom" content={<p className="max-w-xs">{tooltipText}</p>} className="px-2.5 py-2">
      <div
        className={`flex items-center gap-2 pl-4 pr-2 py-1.5 rounded-full text-xs font-semibold text-base-5 ${className}`}
      >
        {label}
        <MdOutlineInfo size={16} />
      </div>
    </Tooltip>
  );
};
